import React, { useMemo, useState } from "react";
import CreativeNameDisplay from "../shared/CreativeNameDisplay";

function ListingsPreview({ preview }) {
  const [viewMode, setViewMode] = useState("cards");

  const sampleAdCreative = {
    appName: "Way2news",
    tagline: "India Reads Way2news",
  };

  const getSalaryText = (job) => {
    const minSalary = job?.min_salary;
    const maxSalary = job?.max_salary;
    if (!minSalary && !maxSalary) return null;
    const isNumber = (value) => value !== null && value !== undefined && !isNaN(Number(value));
    
    const formatSalary = (value) => {
      if (isNumber(value)) {
        return `₹${Number(value)}`;
      }
      return String(value);
    };
    
    if (minSalary && maxSalary) {
      return `${formatSalary(minSalary)} - ${formatSalary(maxSalary)}`;
    } else if (minSalary) {
      return formatSalary(minSalary);
    } else if (maxSalary) {
      return formatSalary(maxSalary);
    }
    
    return null;
  };

  const getEmploymentLabel = (value) => {
    if (value === "1") return "Part Time";
    if (value === "2") return "Full Time";
    return value || "Full Time";
  };

  const gallery = preview?.gallery || {};
  const previewLocation = gallery.location || preview?.location || "Location";
  const previewMinSalary = gallery.min_salary ?? preview?.min_salary;
  const previewMaxSalary = gallery.max_salary ?? preview?.max_salary;
  const previewVacancies =
    preview?.impressions ?? preview?.vacancies ?? "99";

  const jobs = useMemo(() => {
    const currentJobData = {
      min_salary: previewMinSalary,
      max_salary: previewMaxSalary
    };
    const currentJobSalary = getSalaryText(currentJobData) || "₹8000 - ₹20000+ / Month";
    
    const currentJob = {
      id: "current",
      company: preview?.head_line || preview?.title || "Your Job Listing",
      salary: currentJobSalary,
      location: previewLocation,
      type: getEmploymentLabel(preview?.position || preview?.employment_type),
      vacancies:
        typeof previewVacancies === "string"
          ? previewVacancies
          : `${previewVacancies} Vacancies`,
      image: preview?.img_url || preview?.img_urls || "",
      isCurrent: true,
    };
    const swiggyJob = {
      id: "swiggy",
      company: "Swiggy Mart",
      salary: "₹8000 - ₹20000+ / Month",
      location: "Narayanguda, Hyderabad",
      type: "Full Time",
      vacancies: "99",
      image: "",
      isCurrent: false,
    };
    return [swiggyJob, currentJob];
  }, [preview, previewMinSalary, previewMaxSalary, previewLocation, previewVacancies]);

  const buttonMode =
    preview?.button_link && preview?.text_in_link
      ? "3"
      : preview?.button_link
      ? "1"
      : preview?.text_in_link
      ? "2"
      : preview?.button_type || "3";
  const showCall = buttonMode === "1" || buttonMode === "3";
  const showWhatsapp = buttonMode === "2" || buttonMode === "3";
  const isSingleAction = showCall !== showWhatsapp;

  return (
    <div className="download-preview">
      <CreativeNameDisplay preview={preview} />
      <div className="preview-inner !bg-black !p-6 !py-4 !rounded-[46px]">
        <div className="bg-gray-800 rounded-lg p-4 mb-3 relative !h-[40%]">
          <div className="flex justify-end gap-2 mb-3">
            <div className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-sm">
              i
            </div>
            <div className="w-6 h-6 flex flex-col justify-center items-center gap-1">
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 mb-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <div className="space-y-2">
                  <div className="h-2 bg-gray-200 rounded"></div>
                  <div className="h-2 bg-gray-200 rounded w-4/5"></div>
                  <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
              <div className="w-8 h-12 border-2 border-gray-300 rounded-lg flex items-center justify-center">
                <div className="w-4 h-6 bg-gray-100 rounded-sm"></div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-white text-xl font-bold mb-1">
              {sampleAdCreative.appName}
            </h3>
            <p className="text-gray-300 text-sm">{sampleAdCreative.tagline}</p>
          </div>

          <div className="absolute right-4 top-4 flex items-center gap-2">
            <button
              type="button"
              className={`px-3 py-1 rounded-full text-2xs ${
                viewMode === "cards"
                  ? "bg-white text-black"
                  : "bg-gray-700 text-gray-200"
              }`}
              onClick={() => setViewMode("cards")}
            >
              Card View
            </button>
            <button
              type="button"
              className={`px-3 py-1 rounded-full text-2xs ${
                viewMode === "list"
                  ? "bg-white text-black"
                  : "bg-gray-700 text-gray-200"
              }`}
              onClick={() => setViewMode("list")}
            >
              List View
            </button>
          </div>
        </div>

        <div className="flex flex-col justify-between !h-[55%] bg-white p-2">
          <div className="mb-2">
            <div className="flex items-center justify-between bg-white rounded-full px-3 py-2 mb-2 border">
              <div className="flex items-center gap-2 text-gray-500 text-xs">
                <span className="font-bold">≡</span>
                <span>Jobs near Kalvakuntla</span>
              </div>
              <div className="flex items-center gap-3 text-gray-500 text-xs">
                <span>×</span>
                <span className="w-4 h-4 bg-green-500 rounded-full"></span>
              </div>
            </div>

            {viewMode === "cards" && (
              <div className="grid grid-cols-2 gap-2 mb-2">
                {jobs.map(job => (
                  <div key={job.id} className="bg-white rounded-xl p-2 border">
                    <div className="bg-orange-500 rounded-lg h-16 mb-2 relative overflow-hidden">
                      {job.image ? (
                        <img
                          src={job.image}
                          alt="job"
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                      <div className="absolute right-1 top-1 bg-gray-200 text-gray-700 text-2xs px-1 rounded">
                        Ad
                      </div>
                    </div>
                    <div className="text-xs font-semibold">{job.company}</div>
                    <div className="text-2xs text-gray-600">{job.salary}</div>
                    <div className="text-2xs text-gray-500 mt-1">
                      {job.location}
                    </div>
                    <div className="flex gap-1 mt-2">
                      <span
                        className={`text-2xs px-2 py-0.5 rounded ${
                          job.type === "Part Time"
                            ? "bg-gray-200 text-gray-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {job.type}
                      </span>
                      <span className="text-2xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        {job.vacancies} vacancies
                      </span>
                    </div>
                    <div className="flex gap-1 mt-2">
                      {showWhatsapp && (
                        <button
                          className={`${
                            isSingleAction ? "w-full" : "flex-1"
                          } bg-green-100 text-green-700 text-2xs py-1 rounded-full`}
                        >
                          WhatsApp
                        </button>
                      )}
                      {showCall && (
                        <button
                          className={`${
                            isSingleAction ? "w-full" : "flex-1"
                          } bg-blue-500 text-white text-2xs py-1 rounded-full`}
                        >
                          Call HR
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {viewMode === "list" && (
              <div className="flex flex-col gap-2 mb-2">
                {jobs.map(job => (
                  <div
                    key={job.id}
                    className="bg-white rounded-xl p-3 flex items-start gap-3 border"
                  >
                    <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center text-white text-xs font-bold overflow-hidden">
                      {job.image ? (
                        <img
                          src={job.image}
                          alt="job"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        "S"
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold">
                        {job.company}
                      </div>
                      <div className="text-2xs text-gray-600">{job.salary}</div>
                      <div className="flex gap-1 mt-2">
                        <span
                          className={`text-2xs px-2 py-0.5 rounded ${
                            job.type === "Part Time"
                              ? "bg-gray-200 text-gray-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {job.type}
                        </span>
                        <span className="text-2xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {job.vacancies} vacancies
                        </span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {showWhatsapp && (
                          <button
                            className={`${
                              isSingleAction ? "w-full" : "flex-1"
                            } bg-green-100 text-green-700 text-2xs py-1 rounded-full`}
                          >
                            WhatsApp
                          </button>
                        )}
                        {showCall && (
                          <button
                            className={`${
                              isSingleAction ? "w-full" : "flex-1"
                            } bg-blue-500 text-white text-2xs py-1 rounded-full`}
                          >
                            Call HR
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-gray-400 text-xs">›</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between  text-2xs">
            <span className="bg-blue-500 text-white rounded-full px-4 py-2">Post Your Job on Way2News</span>
            <span className="text-white">More ›</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListingsPreview;
